import { supabase } from './supabase';

export interface MaterialCategory {
  id: string;
  business_id: string;
  name: string;
  description: string | null;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface Material {
  id: string;
  business_id: string;
  category_id: string | null;
  name: string;
  description: string | null;
  cost: number;
  unit: string;
  created_at: string;
  updated_at: string;
  category?: MaterialCategory;
}

export async function getMaterials(businessId: string) {
  if (!businessId) {
    throw new Error('Unable to load materials: Business ID is required');
  }

  const { data, error } = await supabase
    .from('materials')
    .select(`
      *,
      category:material_categories(*)
    `)
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Material[];
}

export async function getMaterialCategories(businessId: string) {
  if (!businessId) {
    throw new Error('Unable to load categories: Business ID is required');
  }

  const { data, error } = await supabase
    .from('material_categories')
    .select('*')
    .eq('business_id', businessId)
    .order('order_index', { ascending: true });

  if (error) throw error;
  return data as MaterialCategory[];
}

export async function createMaterialCategory(category: Omit<MaterialCategory, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('material_categories')
    .insert([category])
    .select()
    .single();

  if (error) throw error;
  return data as MaterialCategory;
}

async function updateMaterialCategory(id: string, updates: Partial<MaterialCategory>) {
  const { data, error } = await supabase
    .from('material_categories')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as MaterialCategory;
}

async function deleteMaterialCategory(id: string) {
  const { error } = await supabase
    .from('material_categories')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function createMaterial(material: Omit<Material, 'id' | 'created_at' | 'updated_at'>) {
  if (!material.business_id) {
    throw new Error('Unable to create material: Business ID is required');
  }

  // Ensure category_id is properly handled
  const category_id = material.category_id && material.category_id.trim() !== '' 
    ? material.category_id 
    : null;

  const { data, error } = await supabase
    .from('materials')
    .insert([{
      ...material,
      category_id
    }])
    .select()
    .single();

  if (error) throw error;
  return data as Material;
}

export async function updateMaterial(id: string, updates: Partial<Material>) {
  if (!id) {
    throw new Error('Unable to update material: Material ID is required');
  }

  // Validate category_id
  if (updates.category_id === '') {
    updates.category_id = null;
  }

  const { data, error } = await supabase
    .from('materials')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Material;
}

export async function deleteMaterial(id: string) {
  if (!id) {
    throw new Error('Material ID is required');
  }

  const { error } = await supabase
    .from('materials')
    .delete()
    .eq('id', id);

  if (error) throw error;
}